<script>
  import { Link } from "svelte-routing";
  import { authStore } from "./authStore";
  import { login, logout } from "./authService";
  import CountrySelector from "./components/CountrySelector.svelte";
  import { selectedCountry } from './countryStore';
  import { totalRewards } from './rewardsStore';
  import { getApiUrl } from './api';
  import { currentLang, t } from './i18nStore';

  let currentUser = null;
  let isBusiness = false;
  let isAdmin = false;
  let currency = '$';
  let isMobileMenuOpen = false;

  authStore.subscribe(value => {
    currentUser = value.user;
    isBusiness = value.isBusiness;
    isAdmin = value.isAdmin;
  });

  $: if ($selectedCountry) {
      fetchTotal($selectedCountry);
      currency = $selectedCountry === 'GB' ? '£' : $selectedCountry === 'JP' ? '¥' : '$';
  }

  async function fetchTotal(code) {
      try {
          const res = await fetch(getApiUrl(`/rewards/total?countryCode=${code}`));
          const data = await res.json();
          totalRewards.set(data.total || 0);
      } catch(e) { console.error(e); }
  }

  // Dropdown States
  let isAdminOpen = false;
  let isBusinessOpen = false;
  let isMySpaceOpen = false; 
  let isDevOpen = false;

  function toggleMobileMenu() {
    isMobileMenuOpen = !isMobileMenuOpen;
    if (!isMobileMenuOpen) {
      isAdminOpen = false;
      isBusinessOpen = false;
      isMySpaceOpen = false;
      isDevOpen = false;
    }
  }

  function closeMobileMenu() {
    isMobileMenuOpen = false;
    isAdminOpen = false;
    isBusinessOpen = false;
    isMySpaceOpen = false;
    isDevOpen = false;
  }
  
  function handleLogin() {
    closeMobileMenu();
    login();
  }
  
  function handleLogout() {
    closeMobileMenu();
    logout();
  }

  function toggleAdminMenu(e) {
    e.stopPropagation();
    isAdminOpen = !isAdminOpen;
    isBusinessOpen = false;
    isMySpaceOpen = false;
    isDevOpen = false;
  }

  function toggleBusinessMenu(e) {
    e.stopPropagation();
    isBusinessOpen = !isBusinessOpen;
    isAdminOpen = false;
    isMySpaceOpen = false;
    isDevOpen = false;
  }

  function toggleMySpaceMenu(e) {
    e.stopPropagation();
    isMySpaceOpen = !isMySpaceOpen;
    isAdminOpen = false;
    isBusinessOpen = false;
    isDevOpen = false;
  }

  function toggleDevMenu(e) {
    e.stopPropagation();
    isDevOpen = !isDevOpen;
    isAdminOpen = false;
    isBusinessOpen = false;
    isMySpaceOpen = false;
  }
</script>

<!-- Overlay for mobile menu -->
{#if isMobileMenuOpen}
  <div class="overlay" on:click={closeMobileMenu}></div>
{/if}

<nav class="navbar">
  <div class="nav-left">
    <Link to="/" class="nav-item nav-brand" on:click={closeMobileMenu}>SNAP PERK</Link>
    
    <!-- Total Rewards (Desktop) -->
    <div class="rewards-counter desktop-only">
        <Link to="/top-savers" class="rewards-link">
          <span class="label">{$t('nav.total_saved')}:</span>
          <span class="amount">{currency}{$totalRewards.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
        </Link>
    </div>
  </div>

  <button class="hamburger" on:click={toggleMobileMenu} aria-label="Toggle menu">
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
  </button>

  <div class="nav-links-container {isMobileMenuOpen ? 'open' : ''}">
    
    <button class="close-btn" on:click={closeMobileMenu} aria-label="Close menu">&times;</button>
    
    <!-- Total Rewards (Mobile) -->
    <div class="rewards-wrapper mobile-only">
      <Link to="/top-savers" class="rewards-link" on:click={closeMobileMenu}>
        <span class="label">{$t('nav.total_saved')}:</span>
        <span class="amount">{currency}{$totalRewards.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
      </Link>
    </div>

    <!-- GROUP A: Public Discovery -->
    <div class="nav-group">
      <Link to="/" class="nav-item" on:click={closeMobileMenu}>{$t('nav.home')}</Link>
      <Link to="/today-special" class="nav-item" on:click={closeMobileMenu}>{$t('nav.today_special')}</Link>
      <Link to="/tax-calculator" class="nav-item" on:click={closeMobileMenu}>{$t('nav.tax_calculator')}</Link>
      <Link to="/open-account-calculator" class="nav-item" on:click={closeMobileMenu}>{$t('nav.open_account_calculator')}</Link>
      <Link to="/tools" class="nav-item" on:click={closeMobileMenu}>{$t('nav.handy_tools')}</Link>
      <Link to="/it" class="nav-item" on:click={closeMobileMenu}>{$t('nav.it')}</Link>
      <Link to="/bill-analyzer" class="nav-item" on:click={closeMobileMenu}>{$t('nav.bill_analyzer')}</Link>
      
      <!-- Developer Portal Dropdown -->
      <div class="nav-item dropdown">
        <button class="dropdown-toggle" on:click={toggleDevMenu}>
          <span>{$t('nav.dev_portal')}</span>
          <span class="caret">{isDevOpen ? '▲' : '▼'}</span>
        </button>
        {#if isDevOpen}
          <div class="dropdown-menu">
            <Link to="/dev-erd" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.erd')}</Link>
          </div>
        {/if}
      </div>
    </div>

    <!-- GROUP B: Support -->
    <div class="nav-group">
      <Link to="/donate" class="nav-item" on:click={closeMobileMenu}>{$t('nav.donate')}</Link>
      <Link to="/contact" class="nav-item" on:click={closeMobileMenu}>{$t('nav.contact')}</Link>
    </div>

    <!-- GROUP C: User / Auth -->
    <div class="nav-group user-group">
      {#if currentUser}
        <!-- My Space -->
        <div class="nav-item dropdown">
          <button class="dropdown-toggle" on:click={toggleMySpaceMenu}>
            <span>{$t('nav.my_space')}</span>
            <span class="caret">{isMySpaceOpen ? '▲' : '▼'}</span>
          </button>
          {#if isMySpaceOpen}
            <div class="dropdown-menu">
              <Link to="/dashboard" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.my_dashboard')}</Link>
              <Link to={`/achievements/${currentUser.idTokenClaims.oid}`} class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.my_achievements')}</Link>        
              <Link to={`/inbox/${currentUser.idTokenClaims.oid}`} class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.my_inbox')}</Link>
              <Link to={`/favourites/${currentUser.idTokenClaims.oid}`} class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.my_favourites')}</Link>
              <div class="divider"></div>
              <Link to={`/promotions/user/${currentUser.idTokenClaims.oid}`} class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.my_promotions')}</Link>
              <Link to="/scan" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.scan')}</Link>
              <Link to="/products/scan" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.scan_product')}</Link>
            </div>
          {/if}
        </div>

        <!-- Business Tools -->
        {#if isBusiness}
          <div class="nav-item dropdown">
              <button class="dropdown-toggle" on:click={toggleBusinessMenu}>
                <span>{$t('nav.business_tools')}</span>
                <span class="caret">{isBusinessOpen ? '▲' : '▼'}</span>
              </button>
              {#if isBusinessOpen}
                <div class="dropdown-menu">
                  <Link to="/business-profile" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.business_profile')}</Link>
                  <Link to="/scan" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.scan')}</Link>
                  <Link to="/claim" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.claim_premise')}</Link>
                  <Link to="/premises/management" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.premise_mgmt')}</Link>
                  <div class="divider"></div>
                  <Link to="/audience" class="dropdown-item" on:click={closeMobileMenu}>{$t('nav.target_audience')}</Link>
                </div>
              {/if}
          </div>
        {/if}

        <Link to="/memberships" class="nav-item" on:click={closeMobileMenu}>{$t('nav.memberships')}</Link>
        <Link to="/business-plans" class="nav-item" on:click={closeMobileMenu}>{$t('nav.business_plans')}</Link>
        
        <!-- Admin Dropdown -->
        {#if isAdmin}
          <div class="nav-item dropdown">
            <button class="dropdown-toggle" on:click={toggleAdminMenu}>
              <span>{$t('nav.admin')}</span>
              <span class="caret">{isAdminOpen ? '▲' : '▼'}</span>
            </button>
            {#if isAdminOpen}
            <div class="dropdown-menu">
                <Link to="/admin/categories" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.categories')}</Link>
                <Link to="/admin/banks" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.banks')}</Link>
                <Link to="/admin/credit-cards" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.credit_cards')}</Link>
                <Link to="/admin/payment-methods" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.payment_methods')}</Link>
                <Link to="/admin/stores" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.stores')}</Link>
                <Link to="/admin/places" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.places')}</Link>
                <div class="divider"></div>
                <Link to="/admin/messages" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.messages')}</Link>
                <Link to="/admin/subscriptions" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.subscriptions')}</Link>
                <Link to="/admin/users" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.users')}</Link>
                <Link to="/admin/receipt-verification" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.receipt_verification')}</Link>
                
                <Link to="/admin/reports" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.reports')}</Link>
                <Link to="/admin/promotions" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.promotions')}</Link>
                <Link to="/admin/banned-promotions" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.banned_promotions')}</Link>
                <Link to="/admin/questions" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.questions')}</Link>
                <div class="divider"></div>
                <Link to="/admin/invite" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.invite')}</Link>
                <Link to="/admin/scraper" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.web_scraper')}</Link>
                <Link to="/admin/formulas/missing" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.missing_formulas')}</Link>
                <Link to="/admin/broadcast" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.broadcast_message')}</Link>
                <Link to="/admin/ai-models" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.ai_models')}</Link>
                <Link to="/admin/ai-tasks" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.ai_tasks')}</Link>
                <Link to="/admin/logs" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.api_logs')}</Link>
                <Link to="/admin/recommendation-logs" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.recommendation_logs')}</Link>
                <Link to="/admin/search-logs" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.search_logs')}</Link>
                <Link to="/admin/banners" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.banners')}</Link>
                <Link to="/admin/campaign" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.campaign')}</Link>
                <Link to="/admin/rewards" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.rewards')}</Link>
                <div class="divider"></div>
                <Link to="/admin/cron-jobs" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.cron_jobs')}</Link>
                <Link to="/admin/system-settings" class="dropdown-item" on:click={closeMobileMenu}>{$t('admin.system_settings')}</Link>
              </div>
            {/if}
          </div>
        {/if}
        
        <div class="nav-profile-section">
          <span class="nav-item user-greeting">
            {$t('nav.welcome')}, <Link to="/dashboard" on:click={closeMobileMenu}>{currentUser.name}</Link>!
            {#if isBusiness}
              <span class="business-indicator" title="Business Account">★</span>
            {/if}
          </span>
          <button class="nav-button logout" on:click={handleLogout}>{$t('nav.logout')}</button>
        </div>
      {:else}
        <!-- Logged-out User Links -->
         <div class="login-wrapper">
            <button class="nav-button login" on:click={handleLogin}>{$t('nav.login')}</button>
            <p class="consent-text">
              {$t('nav.consent_pre')} <Link to="/terms" on:click={closeMobileMenu}>{$t('nav.terms')}</Link> {$t('nav.consent_mid')} <Link to="/privacy" on:click={closeMobileMenu}>{$t('nav.privacy')}</Link>{$t('nav.consent_post')}
            </p>
         </div>
      {/if}
    </div>

    <!-- SETTINGS ROW -->
    <div class="nav-group settings-wrapper">
      <div class="settings-row">
        <div class="selector-item">
            <CountrySelector />
        </div>
        <div class="selector-item">
            <select bind:value={$currentLang} class="lang-select" aria-label="Select Language">
            <option value="en">English</option>
            <option value="zh-HK">中文 (HK)</option>
            <option value="ja">日本語</option>
            </select>
        </div>
      </div>
    </div>
  </div>
</nav>

<style>
  /* ... (styles remain the same) ... */
  .navbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 1rem; 
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    height: 60px;
    position: relative; 
    z-index: 100;
  }

  .nav-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .nav-item {
    text-decoration: none;
    color: #333;
    padding: 0 0.5rem;
    white-space: nowrap;
  }
  
  .nav-brand {
    font-weight: bold;
    font-size: 1.5rem;
    padding: 0;
    margin-right: 0.5rem;
  }

  /* Desktop Rewards */
  .rewards-counter {
      display: flex;
      align-items: center;
  }
  .rewards-link {
      text-decoration: none;
      background-color: #e6fffa;
      padding: 0.3rem 0.8rem;
      border-radius: 20px;
      border: 1px solid #38b2ac;
      display: flex;
      gap: 0.5rem;
      align-items: center;
      transition: transform 0.2s;
  }
  .rewards-link:hover { transform: scale(1.05); }
  .label { color: #2c7a7b; font-size: 0.8rem; font-weight: 600; text-transform: uppercase; }
  .amount { color: #234e52; font-weight: bold; font-size: 0.95rem; }

  /* Desktop Menu Container */
  .nav-links-container {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .nav-group {
      display: flex;
      align-items: center;
      gap: 0.5rem;
  }

  /* Buttons */
  .nav-button {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 6px 14px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.9rem;
  }
  .nav-button.logout { background-color: #6c757d; }

  .user-greeting { font-weight: bold; color: #0056b3; font-size: 0.9rem; }
  .user-greeting a { text-decoration: none; color: inherit; }

  .business-indicator {
    color: #ffc107;
    font-size: 1rem;
    margin-left: 2px;
  }
  
  /* Consent Text */
  .consent-text {
      font-size: 0.65rem;
      color: #666;
      margin: 0;
      text-align: center;
      line-height: 1.2;
      max-width: 200px;
  }
  .consent-text :global(a) { color: #007bff; text-decoration: none; }
  .consent-text :global(a:hover) { text-decoration: underline; }

  /* Login Wrapper */
  .login-wrapper { display: flex; flex-direction: column; align-items: center; gap: 0.3rem; }

  /* Desktop Dropdowns */
  .dropdown { position: relative; }
  .dropdown-toggle {
    background: none; border: none; font-family: inherit; font-size: 1rem;
    color: #333; cursor: pointer; padding: 0 0.5rem;
    display: flex; align-items: center;
  }
  .caret { font-size: 0.7rem; margin-left: 4px; }
  .dropdown-menu {
    position: absolute; top: 120%; right: 0;
    background-color: white; box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    border-radius: 5px; padding: 0.5rem 0; list-style: none;
    z-index: 1100; display: flex; flex-direction: column; min-width: 180px;
  }
  .dropdown-item { padding: 0.5rem 1rem; color: #333; text-decoration: none; display: block; }
  .dropdown-item:hover { background-color: #f1f1f1; }
  .divider { border-top: 1px solid #eee; margin: 0.5rem 0; }

  /* Settings Row (Desktop) */
  .settings-row { display: flex; align-items: center; gap: 0.5rem; }
  .lang-select {
      padding: 0.3rem; border-radius: 4px; border: 1px solid #ccc;
      font-size: 0.85rem; background-color: #f8f9fa; cursor: pointer;
  }

  /* Hamburger & Overlay */
  .hamburger { display: none; background: none; border: none; cursor: pointer; padding: 0; }
  .close-btn { display: none; }
  .overlay { display: none; }
  .mobile-only { display: none; }

  /* --- MOBILE BREAKPOINT (992px) --- */
  @media (max-width: 992px) {
    .hamburger { display: block; z-index: 1002; }
    .desktop-only { display: none; }
    .mobile-only { display: flex; }
    
    .overlay {
      display: block; position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0, 0, 0, 0.5); z-index: 999;
      opacity: 0; transition: opacity 0.3s ease; pointer-events: none;
    }
    .nav-links-container.open + .overlay { opacity: 1; pointer-events: auto; }
    
    /* Mobile Sidebar */
    .nav-links-container {
      position: fixed; top: 0; left: 0; width: 85vw; max-width: 350px; height: 100vh;
      background: #fff; box-shadow: 2px 0 10px rgba(0,0,0,0.1);
      transform: translateX(-100%); transition: transform 0.3s ease-out;
      flex-direction: column; align-items: flex-start; gap: 0;
      padding: 4rem 1rem 2rem; z-index: 1001; overflow-y: auto; box-sizing: border-box;
    }
    .nav-links-container.open { transform: translateX(0); }
    .close-btn { display: block; background: none; border: none; font-size: 2rem; color: #333; position: absolute; top: 10px; right: 15px; }

    /* Mobile Groups & Items */
    .nav-group {
        display: flex; flex-direction: column; width: 100%;
        border-bottom: 1px solid #eee; padding-bottom: 0.5rem; margin-bottom: 0.5rem;
    }
    .nav-group:last-child { border-bottom: none; margin-bottom: 0; }
    
    /* Reduced spacing for mobile items */
    .nav-item {
      padding: 0.6rem 0; width: 100%; font-size: 1rem;
    }
    
    .rewards-wrapper {
        width: 100%; justify-content: center; margin-bottom: 1.5rem;
    }
    .rewards-link { width: 100%; justify-content: center; padding: 0.6rem; }

    .nav-profile-section {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 100%; gap: 0.5rem;
    }
    .user-greeting { padding: 0.5rem 0; }
    .nav-button { width: 100%; padding: 0.8rem; margin-top: 0.5rem; }
    
    .login-wrapper {
        align-items: flex-start;
        width: 100%;
        gap: 0.5rem;
    }
    .consent-text { text-align: left; max-width: 100%; }

    /* Mobile Dropdowns */
    .dropdown { width: 100%; }
    .dropdown-toggle {
      width: 100%; padding: 0.6rem 0; justify-content: space-between;
    }
    .dropdown-menu {
      position: static; box-shadow: none; border: none; padding: 0 0 0 1rem; width: 100%;
    }
    .dropdown-item { padding: 0.5rem 0; border-bottom: 1px solid #f9f9f9; font-size: 0.95rem; }

    /* Mobile Settings Row (Bottom) */
    .settings-wrapper {
        margin-top: auto; /* Push to bottom if flex container has height */
        padding-top: 1rem; border-bottom: none;
    }
    .settings-row { width: 100%; justify-content: space-between; gap: 0.5rem; }
    .selector-item { flex: 1; }
    :global(.country-selector select), .lang-select { width: 100%; height: 40px; padding: 0.2rem 0.5rem; }
  }
</style>